import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  billingStatusQueryKey,
  type CheckoutConfigRequest,
  checkoutConfigQueryKey,
  getBillingStatusEffect,
  getCheckoutConfigEffect,
  getPaymentDetailsEffect,
  getPaymentSubscriptionsEffect,
  getReferralListEffect,
  getUserPreferencesEffect,
  getUserProfileEffect,
  getUserSummaryEffect,
  paymentDetailsQueryKey,
  paymentSubscriptionsQueryKey,
  referralListQueryKey,
  setReferredByCodeEffect,
  type UpdateUserProfilePayload,
  type UserPreferencesUpdates,
  updateUserPreferencesEffect,
  updateUserProfileEffect,
  userPreferencesQueryKey,
  userProfileQueryKey,
  userSummaryQueryKey,
} from './user';
import { useEq } from './runtime-context';
import type { Effect } from 'effect';
import { buildUserPreferencesLifecycle } from './user-preferences-lifecycle';

// Shared user / metering / payment query hooks + profile/preferences mutations
// (start / mobile / browser-ext). The effects + keys + types live in `./user`
// (React-free, so the browser-ext service worker can import it); the React hooks
// live here and resolve the runtime via `useEq()` (AppRuntimeProvider).
//
// Auth-gating (whether to fire the query) is app-specific — each app passes
// `{ enabled }` from its own session hook — so these hooks stay gate-agnostic
// and default to enabled.

const STALE_TIME_MS = 60 * 60 * 1000; // 1h

// —— eq-agnostic query args (route loaders wrap with `getEq().queryOptions`) ——

export const userSummaryArgs = {
  queryKey: userSummaryQueryKey,
  queryFn: () => getUserSummaryEffect,
  staleTime: STALE_TIME_MS,
} as const;

export const paymentSubscriptionsArgs = {
  queryKey: paymentSubscriptionsQueryKey,
  queryFn: () => getPaymentSubscriptionsEffect,
  staleTime: STALE_TIME_MS,
} as const;

export const paymentDetailsArgs = {
  queryKey: paymentDetailsQueryKey,
  queryFn: () => getPaymentDetailsEffect,
  staleTime: STALE_TIME_MS,
} as const;

export const userProfileArgs = {
  queryKey: userProfileQueryKey,
  queryFn: () => getUserProfileEffect,
  staleTime: STALE_TIME_MS,
} as const;

export const userPreferencesArgs = {
  queryKey: userPreferencesQueryKey,
  queryFn: () => getUserPreferencesEffect,
  staleTime: STALE_TIME_MS,
} as const;

export const referralListArgs = {
  queryKey: referralListQueryKey,
  queryFn: () => getReferralListEffect,
  staleTime: STALE_TIME_MS,
} as const;

export const billingStatusArgs = {
  queryKey: billingStatusQueryKey,
  queryFn: () => getBillingStatusEffect,
  staleTime: STALE_TIME_MS,
} as const;

/**
 * Deliberately *not* an hour stale on the paywall: the offer carries an
 * experiment assignment and a promo evaluation, and a purchase surface
 * rendering an hour-old offer is how a user is shown one price and charged
 * another. The server re-reads both when the subscription is created, so a
 * stale render cannot buy the wrong thing — it can only confuse. The plan page
 * buys nothing from this offer, since a plan change confirms against its own
 * server preview, so it keeps the hour; so does the `offer` preview, whose
 * buy button leads to the paywall's own fresh read.
 */
export const checkoutConfigArgs = (request: CheckoutConfigRequest) =>
  ({
    queryKey: checkoutConfigQueryKey(request),
    queryFn: () => getCheckoutConfigEffect(request),
    staleTime: request.surface === 'paywall' ? 0 : STALE_TIME_MS,
  }) as const;

// —— Read hooks ——

export const useUserSummaryQuery = (opts?: { enabled?: boolean }) =>
  useQuery({
    ...useEq().queryOptions(userSummaryArgs),
    enabled: opts?.enabled ?? true,
  });

export const usePaymentSubscriptionsQuery = () =>
  useQuery(useEq().queryOptions(paymentSubscriptionsArgs));

export const useBillingStatusQuery = (opts?: { enabled?: boolean }) =>
  useQuery({
    ...useEq().queryOptions(billingStatusArgs),
    enabled: opts?.enabled ?? true,
  });

export const useCheckoutConfigQuery = ({
  enabled = true,
  ...request
}: CheckoutConfigRequest & { enabled?: boolean } = {}) =>
  useQuery({
    ...useEq().queryOptions(checkoutConfigArgs(request)),
    enabled,
  });

export const usePaymentDetailsQuery = () =>
  useQuery(useEq().queryOptions(paymentDetailsArgs));

export const useUserProfileQuery = () =>
  useQuery(useEq().queryOptions(userProfileArgs));

export const useUserPreferencesQuery = (opts?: { enabled?: boolean }) =>
  useQuery({
    ...useEq().queryOptions(userPreferencesArgs),
    enabled: opts?.enabled ?? true,
  });

export const useReferralListQuery = () =>
  useQuery(useEq().queryOptions(referralListArgs));

// —— Mutations (optimistic cache write + invalidate; app callsites add toasts) ——

export const useUpdateUserPreferencesMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    ...useEq().mutationOptions({
      mutationFn: (updates: UserPreferencesUpdates) =>
        updateUserPreferencesEffect(updates),
    }),
    ...buildUserPreferencesLifecycle(queryClient),
  });
};

export const usePatchUserProfileMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    ...useEq().mutationOptions({
      mutationFn: (changes: UpdateUserProfilePayload) =>
        updateUserProfileEffect(changes),
    }),
    onSuccess: (_, changes) => {
      queryClient.setQueryData(
        userProfileQueryKey,
        (old: Effect.Success<typeof getUserProfileEffect> | undefined) =>
          old ? { ...old, ...changes } : old,
      );
      return queryClient.invalidateQueries({ queryKey: userProfileQueryKey });
    },
  });
};

export const useSetReferredByCodeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    ...useEq().mutationOptions({
      mutationFn: (referralCode: string) =>
        setReferredByCodeEffect(referralCode),
    }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userSummaryQueryKey });
      return queryClient.invalidateQueries({ queryKey: referralListQueryKey });
    },
  });
};
